import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type DragEvent } from 'react';
import { Upload, FileCheck2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { formatDateShort } from '@/lib/format';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/import')({
  component: ImportPage,
});

function ImportPage() {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lastReport, setLastReport] = useState<{ total: number; new: number; duplicates: number } | null>(null);

  const { data: imports } = useQuery({ queryKey: ['imports'], queryFn: api.listImports });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadCamt(file),
    onSuccess: (r) => {
      setLastReport({ total: r.total, new: r.new, duplicates: r.duplicates });
      toast.success(`${r.new} nouvelles transactions`, { description: `${r.duplicates} duplicatas` });
      void qc.invalidateQueries({ queryKey: ['imports'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Erreur';
      toast.error('Import échoué', { description: msg });
    },
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    upload.mutate(file);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    onFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold">Import CAMT.053</h1>
        <p className="text-sm text-muted-foreground">
          Glisse-dépose ton export UBS E-Banking (XML camt.053). Les fichiers déjà importés sont rejetés automatiquement.
        </p>
      </div>

      <Card
        className={cn(
          'border-2 border-dashed transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-border',
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <CardContent className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <Upload className="h-10 w-10 text-muted-foreground" />
          <div>
            <p className="font-medium">Dépose ton fichier ici</p>
            <p className="text-sm text-muted-foreground">ou clique pour parcourir</p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xml,.camt053,.camt"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <Button onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? 'Import en cours…' : 'Choisir un fichier'}
          </Button>
        </CardContent>
      </Card>

      {lastReport ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileCheck2 className="h-4 w-4 text-success" /> Dernier import
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4 text-center">
            <Stat label="Total" value={lastReport.total} />
            <Stat label="Nouvelles" value={lastReport.new} highlight />
            <Stat label="Duplicatas" value={lastReport.duplicates} muted />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Historique imports</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Fichier</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2 text-right">Nouvelles</th>
                  <th className="px-4 py-2 text-right">Duplicatas</th>
                  <th className="px-4 py-2 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {(imports as Array<{
                  id: string;
                  filename: string;
                  totalTransactions: number;
                  newTransactions: number;
                  duplicates: number;
                  status: string;
                  createdAt: string;
                }> | undefined)?.map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="px-4 py-2 num-tnum">{formatDateShort(b.createdAt)}</td>
                    <td className="px-4 py-2 truncate max-w-[30ch]">{b.filename}</td>
                    <td className="px-4 py-2 text-right num-tnum">{b.totalTransactions}</td>
                    <td className="px-4 py-2 text-right num-tnum text-success">{b.newTransactions}</td>
                    <td className="px-4 py-2 text-right num-tnum text-muted-foreground">{b.duplicates}</td>
                    <td className="px-4 py-2">
                      {b.status === 'completed' ? (
                        <span className="text-success">✓ Terminé</span>
                      ) : b.status === 'failed' ? (
                        <span className="text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Échec
                        </span>
                      ) : (
                        <span className="text-warning">…</span>
                      )}
                    </td>
                  </tr>
                ))}
                {(!imports || imports.length === 0) ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      Aucun import pour l'instant
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, highlight, muted }: { label: string; value: number; highlight?: boolean; muted?: boolean }) {
  return (
    <div>
      <div className={cn('text-2xl font-bold num-tnum', highlight && 'text-success', muted && 'text-muted-foreground')}>
        {value.toLocaleString('fr-CH')}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
